import _ from 'underscore'
import Backbone from '@polleverywhere/backbone'
import URI from 'urijs'
import Base from './base.js'
import FlashOptions from './flash_options'
import Leaderboard from './leaderboard'
import Polls from './polls'
import { cleanString } from '../util'
import Instance from './mixins/_instance'
import LiveUpdates from './mixins/_live.js'
import SubModel from './mixins/_sub_model'

function configureFlashOptions () {
  this._flashOptions.set({
    showInstructions: false,
    showTitle: false
  })

  this._flashOptions.url = () => {
    return URI(`/api/competitions/${this.get('permalink')}/visual_setting`).toString()
  }

  return this._flashOptions
}

/**
 * Normalizes the specified dataset and mutates it.
 * @param {Object} attrs
 * @return {Object} Mutated attrs
 */
function normalizeAttrs (attrs = {}) {
  // fix inconsistent collection data from backend
  attrs.polls = attrs.polls || []
  attrs.polls = attrs.polls.map((poll) => {
    poll.presenter = Object.assign({}, attrs.presenter, poll.presenter)
    poll.group_id = null
    poll.survey_id = null
    poll.competition_id = attrs.id
    return poll
  })

  return attrs
}

// Class representing the competition details
export default class Competition extends Base {
  // @name Competition#modelProperties
  // @type String
  // @readonly
  get modelProperties () {
    return [
      'display_state',
      'group_id',
      'id',
      'owner_id',
      'owner_login',
      'permalink',
      'polls',
      'title',
      'timed',
      'time_to_respond',
      'editable_answers',
      'registered_participants_only'
    ]
  }

  /**
   * @param {Object} attrs
   * @param {Object} options All options are passed along into Leaderboard.
   * @param {number} options.participantId Associate this competition's
   *    leaderboard with this particular user.
   */
  initialize (attrs = {}, options = {}) {
    super.initialize.apply(this, arguments)

    this.include(LiveUpdates)
    this.include(SubModel)
    this.include(Instance)
    this.initSubModel('presenter')

    /**
     * Holds reference to a stubbed FlashOptions model because Viz needs one.
     * @type {Backbone.Model.FlashOptions}
     */
    this.initSubModel('flash_options', {
      model: FlashOptions,
      prefix: '_'
    }).poll = this
    this.flashOptionsStub = configureFlashOptions.call(this)

    /**
     * Holds reference to the competition's leaderboard collection to fetch
     * participant scores from the leaderboard API endpoint. Pass along all
     * constructor options.
     * @type {Backbone.Collection.Leaderboard}
     */
    this.leaderboard = new Leaderboard(null, Object.assign({}, options, {
      competition: this
    }))

    this.polls = new Polls()
    this.polls.comparator = 'position'
    this.polls.competition = this

    this.parse(attrs, options)
  }

  url () {
    return this.instance.url()
  }

  // @returns {promise}
  deactivate () {
    return Backbone.ajax({
      url: '/api/activation',
      method: 'DELETE',
      data: {
        user_id: this.get('owner_id')
      }
    }).then(() => {
      this.presenter.set('active', false)
      this.set('current', false)
    })
  }

  // @returns {promise}
  activate () {
    const $xhr = Backbone.ajax({
      url: '/api/activation',
      method: 'PUT',
      data: {
        type: this.type(),
        uid: this.get('permalink')
      }
    })

    $xhr.done(() => {
      this.presenter.set('active', true)
      this.set('current', true)
    })

    return $xhr
  }

  // @returns {string} The model type, should always be competition.
  type () {
    if (this.get('type') !== 'competition') {
      this.set({ type: 'competition' }, { silent: true })
    }

    return this.get('type')
  }

  // @returns {string}
  cleanTitle (opts = {}) {
    return cleanString(this.get('title'), opts)
  }

  // @returns {string}
  showUrl () {
    if (!(this.has('permalink'))) return ''

    return URI('/competitions').segment(this.get('permalink')).toString()
  }

  // @returns {string}
  copyUrl () {
    if (!(this.has('id'))) return ''

    return URI('/api/competitions')
      .segment(this.get('id').toString())
      .segment('copy').toString()
  }

  // @param {object} options - These are backbone options
  // @returns {object}
  toJSON (options = {}) {
    const obj = this.pick(this.modelProperties)

    obj.polls = this.polls.invoke('toJSON', { root: false })
      .filter(poll => !_.isEmpty(poll))

    return obj
  }

  parse (attrs = {}, options = {}) {
    if (this.instance) {
      // All instance data must be normalized to support legacy keys.
      attrs = this.instance.normalizeActivityData(attrs)
    }

    if (this.polls && attrs.polls) {
      attrs = normalizeAttrs(attrs)
      this.polls.set(attrs.polls, options)

      // Remove the polls key from model attrs to ensure the polls collection is
      // the single source of truth for subpoll data.
      delete attrs.polls
    }

    return attrs
  }

  /**
   * Returns whether competition is restricted to registered participants only.
   * @return {boolean} True if restriction is enabled. False otherwise.
   */
  requiresRegistration () {
    return !!this.get('registered_participants_only')
  }

  /**
   * Returns a stubbed FlashOptions model, primarily for Viz that depends on
   * this being attached to the activity model.
   * @return {Backbone.Model.FlashOptions}
   */
  flashOptions () {
    return this.flashOptionsStub
  }

  /**
   * Returns the competition's intro text description as an array of strings
   * with each item representing a line of text to display. The content is
   * typically used to add additional information or taglines for this MQA.
   * @return {string[]} Array list of text strings.
   */
  introTextList () {
    // TODO: Competitions - If we decide to make this user configurable, we will
    // replace this with an appropriate model attribute hydrated by backend.
    const textList = ['Get ready to compete!']

    if (this.get('timed')) {
      textList.push(
        'Win up to 1,000 points per answer!',
        'Speed counts, so move fast!'
      )
    } else {
      textList.push(
        'Win 1,000 points per correct answer!'
      )
    }

    return textList
  }

  /**
   * @return {string}
   */
  liveChannel () {
    if (!this.has('permalink')) { throw new Error('This competition does not exist.') }
    return `competitions/${this.get('permalink')}/firehose.json`
  }

  /**
   * Parse Firehose response into the model.
   * @param {object} data Firehose response data.
   * @return {object} Returns potentially mutated data.
   */
  processLiveResponse (data = {}) {
    // Always update the activity for every Firehose update.
    this.set(this.parse(data))
    return data
  }

  /**
   * Returns whether the competition is currently activated.
   * @return {boolean}
   */
  isActivated () {
    return this.get('current')
  }

  isMQA () {
    return true
  }

  mqaType () {
    return 'competition'
  }
}
